﻿<script setup lang="ts">
    import { reactive, onMounted, watch, toRaw } from 'vue';
    import { loadMediaMonitor, updateMediaMonitor, MediaMonitorReference, UpdateMediaMonitor } from '@views/MediaMonitors/MediaMonitors.access';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import { useRouter } from 'vue-router';
    import { deepClone } from '@shared/utils';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import GeneralSettings from '@views/MediaMonitors/MediaMonitorGeneralSettings.vue';
    import SearchSettings from '@views/MediaMonitors/MediaMonitorSearchSettings.vue';
    import Hits from '@views/MediaMonitors/MediaMonitorHits.vue';
    import Loading from '@shared/components/Loading.vue';

    const router = useRouter();

    let mediaMonitorRaw: MediaMonitorReference | null = null;

    const state = reactive<{
        mediaMonitor: MediaMonitorReference | null;
        loading: boolean;
        id: string | null;
        hasChanges: boolean;
        saving: boolean;
        validate: boolean;
        isDemoMode: boolean;
    }>({
        mediaMonitor: null,
        loading: true,
        id: null,
        hasChanges: false,
        saving: false,
        validate: false,
        isDemoMode: false,
    });

    onMounted(async () => {
        state.id = router.currentRoute.value.params.id as string;

        let query = router.currentRoute.value.query;
        state.isDemoMode = query.demo === 'true' ? true : false;

        await loadMediaMonitor(state.id, state.isDemoMode).then((data: any) => {
            mediaMonitorRaw = deepClone(data);
            state.mediaMonitor = data;
            state.loading = false;
        });
    });

    watch(
        () => state.mediaMonitor,
        (newValue) => {
            if (newValue === null || JSON.stringify(newValue) === JSON.stringify(mediaMonitorRaw)) return;
            state.hasChanges = true;
        },
        { deep: true }
    );

    const saveChanges = () => {
        state.validate = true;
        if (!state.mediaMonitor || state.saving || state.mediaMonitor.keywords.length === 0) return;
        state.saving = true;

        let updatedData: UpdateMediaMonitor = {
            id: state.mediaMonitor.id,
            active: state.mediaMonitor.active,
            description: state.mediaMonitor.description,
            name: state.mediaMonitor.name,
            folder: null,
            show_on_mobile: true,
            feeds_rss_enabled: false,
            feeds_rss_title: null,
            feeds_html_enabled: false,
            feeds_html_title: null,
            stack_similar_hits_enabled: false,
            stack_similar_hits_condition: 'title',
            stack_similar_hits_content_percent: 1,
            stack_similar_hits_title_percent: 1,
            keywords: state.mediaMonitor.keywords,
            areas: state.mediaMonitor.areas,
            keywords_condition: state.mediaMonitor.keywords_condition,
            additional_keywords: [],
            block_keywords: [],
            ignore_keywords: [],
            title_keywords: [],
            title_keywords_condition: '',
            title_block_keywords: [],
            title_block_keywords_condition: '',
            url_keywords: [],
            url_block_keywords: [],
        };

        updateMediaMonitor(updatedData).then((data: UpdateMediaMonitor) => {
            mediaMonitorRaw = deepClone(toRaw(state.mediaMonitor) as MediaMonitorReference);
            state.validate = false;
            state.saving = false;
            state.hasChanges = false;
        });
    };
</script>

<script lang="ts"></script>

<template>
    <div class="p-lg-r p-lg-l" v-if="mediaMonitorRaw">
        <p class="fw-600 m-lg-t m-xs-b">
            <router-link :to="{ name: 'MediaMonitors' }">
                <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
                {{ $Resources.MediaMonitorsHeading }}
            </router-link>
        </p>
        <h1 class="m-0-t">
            {{ mediaMonitorRaw.name }}
            <small class="m-sm-l" v-show="mediaMonitorRaw.active">
                <i class="fa fa-circle status-circle status-active m-xs-r" aria-hidden="true"></i>
                {{ $Resources.MediaMonitorActiveLabel }}
            </small>
            <small class="m-sm-l" v-show="!mediaMonitorRaw.active">
                <i class="fa fa-circle status-circle o-40 m-xs-r" aria-hidden="true"></i>
                {{ $Resources.MediaMonitorPausedLabel }}
            </small>
        </h1>

        <p>{{ mediaMonitorRaw.description }}</p>

        <p class="meta m-lg-b">
            <span class="glyphicon glyphicon-time"></span>
            {{ $Resources.MediaMonitorCreatedAt }}
            <time :datetime="mediaMonitorRaw.created_at">{{ formatDateTime(mediaMonitorRaw.created_at) }}</time>
            <span class="glyphicon glyphicon-time m-lg-l"></span>
            {{ $Resources.MediaMonitorEditedAt }}
            <time :datetime="mediaMonitorRaw.modified_at">{{ formatDateTime(mediaMonitorRaw.modified_at) }}</time>
        </p>
    </div>

    <div v-if="state.mediaMonitor">
        <tabs navClass="tabs list-unstyled m-lg-r m-lg-l">
            <tab :title="$Resources.MediaMonitorsHeading">
                <Hits :id="state.id as string" :init="true" :isDemoMode="state.isDemoMode"></Hits>
            </tab>
            <tab :title="$Resources.MediaMonitorHitsSettingsTab">
                <form class="p-lg-r p-lg-l" v-if="state.mediaMonitor" @submit.prevent="saveChanges">
                    <GeneralSettings
                        v-model:active="state.mediaMonitor.active"
                        v-model:name="state.mediaMonitor.name"
                        v-model:description="state.mediaMonitor.description"></GeneralSettings>

                    <SearchSettings
                        :validate="state.validate"
                        v-model:keywords="state.mediaMonitor.keywords"
                        v-model:areas="state.mediaMonitor.areas"
                        v-model:keywords_condition="state.mediaMonitor.keywords_condition"></SearchSettings>

                    <div class="form-group d-flex flex-wrap gap-sm">
                        <router-link :to="{ name: 'MediaMonitors' }" class="btn btn-default m-auto-l">{{ $Resources.MediaMonitorsBackBtn }}</router-link>
                        <button
                            type="submit"
                            class="btn btn-cta p-lg-r p-lg-l"
                            :class="{
                                'is-busy': state.saving,
                            }"
                            :disabled="state.isDemoMode || !state.hasChanges || state.loading">
                            {{ $Resources.MediaMonitorSaveChangesBtn }}
                        </button>
                    </div>
                </form>
            </tab>
        </tabs>
    </div>

    <div class="m-lg-t"><Loading :show="state.loading" :fixed="true"></Loading></div>
</template>

<style lang="less">
    .max-w-600 {
        max-width: 600px;
    }
</style>

<style scoped lang="less"></style>
